import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {updateFeedColumnsSchema} from 'common/src/modules/contentFeeds/store/contentFeedPage/thunks/updateFeedColumnsSchema.thunk';
import {
  ColumnDataType,
  ColumnPurpose,
  ContentFeedColumnDefinition,
  ContentFeedColumnsSchema,
} from 'common/src/modules/contentFeeds/contentFeeds.types';
import assertAiGeneratedContentFeed, {
  CONTENT_FEED_AI_TOOL_CONTEXT_ERROR,
} from '../utils/assertAiGeneratedContentFeed';
import {ContentFeedAiGeneratedSkillToolName} from '../skills/contentFeedAiGeneratedSkill.enums';

const columnDataTypeSchema = z.nativeEnum(ColumnDataType);
const columnPurposeSchema = z.nativeEnum(ColumnPurpose);

const inputSchema = z.object({
  columnKey: z.string().describe('Stable key for the column (unique within the feed)'),
  type: columnDataTypeSchema.describe('Column data type'),
  isRequired: z.boolean().optional().describe('Whether this column is required'),
  purpose: columnPurposeSchema.optional().nullable().describe('Column purpose (Input, Reporting, etc.)'),
  description: z.string().optional().nullable().describe('Human-readable description of the column'),
  isUsedInCreative: z.boolean().optional().describe('Whether this column is used by the creative/template'),
});

export default function addOrUpdateFeedColumnTool(store: CLStore) {
  async function execute({
    input: {columnKey, type, isRequired, purpose, description, isUsedInCreative},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const feed = state.contentFeedPage?.feed;

    if (!assertAiGeneratedContentFeed(feed)) {
      return toolError(CONTENT_FEED_AI_TOOL_CONTEXT_ERROR);
    }

    const existingColumns = feed.feedColumnsSchema?.columns ?? [];
    const nextColumns: ContentFeedColumnDefinition[] = [...existingColumns];
    const idx = nextColumns.findIndex((c) => c.columnKey === columnKey);

    const next: ContentFeedColumnDefinition = {
      ...(idx >= 0 ? nextColumns[idx] : {}),
      columnKey,
      type,
      ...(isRequired !== undefined && {isRequired}),
      ...(purpose !== undefined && {purpose}),
      ...(description !== undefined && {description}),
      ...(isUsedInCreative !== undefined && {isUsedInCreative}),
    };

    if (idx >= 0) {
      nextColumns[idx] = next;
    } else {
      nextColumns.push(next);
    }

    const schema: ContentFeedColumnsSchema = {
      ...feed.feedColumnsSchema,
      columns: nextColumns,
    };

    try {
      await store.dispatch(updateFeedColumnsSchema({feedId: feed.id, schema})).unwrap();

      return toolResult(
        `Successfully ${idx >= 0 ? 'updated' : 'added'} feed column "${columnKey}" (${type}).`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);

      return toolError(`Error updating feed columns schema: ${errorMessage}`);
    }
  }

  return tool({
    displayName: 'Adding or updating feed column',
    name: ContentFeedAiGeneratedSkillToolName.AddOrUpdateFeedColumn,
    description:
      'Adds or updates a single feed column definition by columnKey. Other columns are preserved. Use for input columns and column metadata; generation setup for creative outputs is configured via updateCreativeColumnGenerationSetup.',
    execute,
    inputSchema,
  });
}
